/* eslint-disable sort-imports */
/* eslint-disable valid-jsdoc */
/**
 * documents.js v2.3
 * Document search and filter functionality
 * Improved with more DRY principles
 */
import 'choices.js'
import { fetchNordJson, mapJsonAssoc, trimObj } from './documents__helper'
import {
  bold,
  downloadIcon,
  lang,
  title,
  versionNumber
} from './documents__formatter'
import {
  apiPath,
  plDocuments,
  productgroupsPath,
  productsPath,
  setValidProductGroupNames,
  setValidProductNames,
  setValidSubproductNames,
  subproductsPath,
  validProductGroupNames,
  validProductNames,
  validSubproductNames
} from './documents__data'

// ---- Helper Functions ----

/**
 * Pads a number with leading zeros
 * @param {number} num - The number to pad
 * @param {number} size - Target length
 * @returns {string} - Padded number string
 */
function padNumber(num, size = 2) {
  let s = String(num)
  while (s.length < size) {
    s = `0${s}`
  }
  return s
}

/**
 * Helper function to safely check if a value exists in an array
 * @param {Array} array - Array to check
 * @param {any} value - Value to find
 * @returns {boolean} - Whether value exists in array
 */
function hasValue(array, value) {
  return Array.isArray(array) && array.indexOf(value) !== -1
}

/**
 * Adds a value to an array if it doesn't already exist
 * @param {Array} array - Target array
 * @param {any} value - Value to add
 * @returns {Array} - Updated array
 */
function addUniqueValue(array, value) {
  if (!hasValue(array, value)) {
    array.push(value)
  }
  return array
}

/**
 * Safely extracts values from an array property of an object
 * @param {Object} obj - Source object
 * @param {string} prop - Property name to extract from
 * @param {Array} targetArray - Array to add values to
 * @returns {Array} - Updated target array
 */
function extractUniqueValues(obj, prop, targetArray = []) {
  if (!obj || !Array.isArray(obj[prop])) {
    return targetArray
  }

  obj[prop].forEach((value) => {
    addUniqueValue(targetArray, value)
  })

  return targetArray
}

/**
 * Maps an array of values to choices.js format objects
 * @param {Array} valuesArray - Array of values
 * @returns {Array} - Array of {value, label} objects
 */
function mapToChoicesFormat(valuesArray) {
  return valuesArray.map((value) => ({
    value,
    label: value
  }))
}

/**
 * Creates a filter object for the tabulator table
 * @param {string} field - Field name to filter on
 * @param {string} type - Filter type
 * @param {any} value - Filter value
 * @returns {Object} - Filter object
 */
function createFilter(field, type, value) {
  return { field,
    type,
    value }
}

// ---- Tabulator Filter Extensions ----

// Extend Tabulator with custom filter types
if (typeof Tabulator !== 'undefined') {
  Tabulator.prototype.extendModule('filter', 'filters', {
    contain(filterVal, rowVal) {
      if (!rowVal || !filterVal) {
        return false
      }

      // Convert string to array if needed
      if (!Array.isArray(rowVal)) {
        rowVal = rowVal.split(',')
      }
      return rowVal.includes(filterVal)
    },

    notcontain(filterVal, rowVal) {
      if (!rowVal || !filterVal) {
        return true
      }

      // Convert string to array if needed
      if (!Array.isArray(rowVal)) {
        rowVal = rowVal.split(',')
      }
      return !rowVal.includes(filterVal)
    }
  })
}

// ---- Configuration ----

// Select dropdown configuration
const selectConfig = {
  removeItemButton: true,
  placeholder: true,
  searchPlaceholderValue: plDocuments.dataset.i18nSearchplaceholderValue,
  itemSelectText: '',
  noChoicesText: plDocuments.dataset.i18nNochoicestext
}

// ---- DOM Elements & Selectors ----

// Define selectors for different filter types
const SELECTOR_MAPPING = {
  products: '#products',
  subproducts: '#subproducts',
  industries: '#industries',
  productgroups: '#productgroups',
  certificates: '#certificates',
  certificatetypes: '#certificatetypes',
  certificateproducers: '#certificateproducers',
  flyernames: '#flyernames',
  documentsSearch: '#documentssearch'
}

// Get DOM elements using selector mapping
const domElements = Object.entries(SELECTOR_MAPPING).reduce((acc, [key, selector]) => {
  acc[`${key}El`] = document.querySelector(selector)
  return acc
}, {})

// Destructure for easier access
const {
  productsEl, subproductsEl, industriesEl, productgroupsEl,
  certificatesEl, certificatetypesEl, certificateproducersEl,
  flyernamesEl, documentsSearchEl
} = domElements

// All select elements
const selects = document.getElementsByTagName('select')

// ---- Selector Instances ----

// Choice selector instances
const selectorInstances = {}

// ---- Data Containers ----

let filteredData
let filteredDataRows
const jsonData = {
  products: null,
  subproducts: null,
  productgroups: null
}

// Settings
const currentCountry = typeof settings !== 'undefined' && settings.country ? settings.country : false

// ---- API Fetching Options ----

const fetchOptions = {
  method: 'GET',
  mode: 'no-cors',
  credentials: 'same-origin',
  headers: {
    'Content-Type': 'application/json'
  },
  redirect: 'follow',
  referrerPolicy: 'no-referrer'
}

// ---- Functions ----

/**
 * Helper function to toggle the state of a Choices.js selector
 * @param {string} selectorName - Name of the selector (key in selectorInstances)
 * @param {Array} dataArray - Data array to determine state
 * @param {string} emptyDataMessage - Optional message to log if empty
 */
function toggleSelectorState(selectorName, dataArray, emptyDataMessage = null) {
  const selector = selectorInstances[selectorName]
  if (!selector) {
    return
  }

  if (dataArray && dataArray.length > 0) {
    selector.enable()
  } else {
    selector.disable()
    if (emptyDataMessage && typeof console !== 'undefined') {
      console.log(emptyDataMessage)
    }
  }
}

/**
 * Gets the currently selected value from a select element
 * @param {HTMLElement} selectEl - The select element
 * @returns {string} - Selected value or empty string
 */
function getSelectValue(selectEl) {
  return selectEl?.options?.[selectEl?.selectedIndex]?.value || ''
}

/**
 * Updates related product data based on current selections
 */
function updateRelatedProductData() {
  const productsVal = getSelectValue(productsEl)
  const subproductsVal = getSelectValue(subproductsEl)
  const productgroupsVal = getSelectValue(productgroupsEl)

  // Update based on selected product
  if (productsVal && jsonData.products?.[productsVal]) {
    setValidProductGroupNames(jsonData.products[productsVal].productGroupNames)
  }

  // Update based on selected subproduct
  if (subproductsVal && jsonData.subproducts?.[subproductsVal]) {
    setValidProductNames(jsonData.subproducts[subproductsVal].productNames)
  }

  // Update based on selected product group
  if (productgroupsVal && jsonData.productgroups?.[productgroupsVal]) {
    setValidProductNames(jsonData.productgroups[productgroupsVal].productNames)
    updateProductsDropdown(productgroupsVal)
  }
}

/**
 * Creates filter objects from select values for the table
 * @returns {Array} Array of filter objects
 */
function createFilters() {
  const filter = []

  // Get current values from selectors
  const documentssearchVal = documentsSearchEl?.value || ''
  const productsVal = getSelectValue(productsEl)
  const subproductsVal = getSelectValue(subproductsEl)
  const industriesVal = getSelectValue(industriesEl)
  const productgroupsVal = getSelectValue(productgroupsEl)
  const certificatesVal = getSelectValue(certificatesEl)
  const certificatetypesVal = getSelectValue(certificatetypesEl)
  const certificateproducersVal = getSelectValue(certificateproducersEl)
  const flyernamesVal = getSelectValue(flyernamesEl)

  // Update related product lists
  updateRelatedProductData()

  // Add search filter
  if (documentssearchVal) {
    filter.push([
      createFilter('description', 'like', documentssearchVal),
      createFilter('type', 'like', documentssearchVal),
      createFilter('partNumber', 'like', documentssearchVal)
    ])
  }

  // Add filters for all selectable fields - only if a value is selected
  const filterMappings = [
    { value: productsVal,
      field: 'products',
      type: 'contain' },
    { value: subproductsVal,
      field: 'subproducts',
      type: 'contain' },
    { value: productgroupsVal,
      field: 'productGroups',
      type: 'contain' },
    { value: industriesVal,
      field: 'industries',
      type: 'contain' },
    { value: certificatesVal,
      field: 'certificateGroup',
      type: '=' },
    { value: certificateproducersVal,
      field: 'certificateProducer',
      type: '=' },
    { value: certificatetypesVal,
      field: 'certificateType',
      type: '=' },
    { value: flyernamesVal,
      field: 'flyerName',
      type: '=' }
  ]

  // Add filters for all non-empty values
  filterMappings.forEach(({ value, field, type }) => {
    if (value) {
      filter.push(createFilter(field, type, value))
    }
  })

  // Country filter is always applied if available
  if (currentCountry) {
    filter.push(createFilter('excludedCountries', 'notcontain', currentCountry))
  }

  return filter
}

/**
 * Updates the products dropdown based on selected product group
 * @param {string} productgroupVal - Selected product group value
 */
function updateProductsDropdown(productgroupVal = null) {
  // If no value provided, get it from the select element
  const groupVal = productgroupVal || getSelectValue(productgroupsEl)
  const productGroup = jsonData.productgroups?.[groupVal]

  if (!productGroup || !productGroup.show) {
    return
  }

  // Filter out null and empty values
  if (Array.isArray(productGroup.productNames)) {
    const filteredProducts = productGroup.productNames.filter((product) => product && product !== 'null')
    const productsObj = mapToChoicesFormat(filteredProducts)

    if (selectorInstances.products) {
      selectorInstances.products.setChoices(productsObj, 'value', 'label', true).enable()
    }
  }
}

/**
 * Updates the filter and refreshes the table with filtered data
 */
function updateFilter() {
  const filters = createFilters()

  // Apply filters to table
  table.clearFilter()
  if (filters.length) {
    table.setFilter(filters)
  }

  // Update filtered data from table
  filteredDataRows = table.getRows('active')
  filteredData = filteredDataRows.map((row) => row.getData())

  // Update select options based on filtered data
  updateSelectFilters(filteredData)
}

/**
 * Process document data to extract filter values
 * @param {Array} data - Document data array
 */
function updateSelectFilters(data) {
  // Arrays to store unique values
  const collections = {
    products: [],
    subproducts: [],
    industries: [],
    productgroups: [],
    certificates: [],
    certificateproducers: [],
    certificatetypes: [],
    flyernames: []
  }

  // Extract unique values from document data
  data.forEach((doc) => {
    // Extract array properties
    ['products', 'subproducts', 'industries'].forEach((prop) => {
      extractUniqueValues(doc, prop, collections[prop])
    })

    // Extract product groups with check for 'show' property
    if (Array.isArray(doc.productGroups)) {
      doc.productGroups.forEach((group) => {
        if (jsonData.productgroups?.[group]?.show === true) {
          addUniqueValue(collections.productgroups, group)
        }
      })
    }

    // Extract single value properties
    ['certificateGroup', 'certificateProducer', 'certificateType', 'flyerName'].forEach((prop) => {
      if (doc[prop]) {
        const collectionName = prop === 'certificateGroup' ? 'certificates'
          : prop === 'certificateProducer' ? 'certificateproducers'
            : prop === 'certificateType' ? 'certificatetypes' : 'flyernames'
        addUniqueValue(collections[collectionName], doc[prop])
      }
    })
  })

  // Apply filters from valid names
  if (Array.isArray(validProductGroupNames)) {
    collections.productgroups = collections.productgroups.filter((group) => validProductGroupNames.includes(group))
  }

  if (Array.isArray(validSubproductNames)) {
    collections.subproducts = collections.subproducts.filter((subproduct) => validSubproductNames.includes(subproduct))
  }

  if (Array.isArray(validProductNames)) {
    collections.products = collections.products.filter((product) => validProductNames.includes(product))
  }

  // Update all selectors with their respective data
  Object.entries(collections).forEach(([key, values]) => {
    const objValues = mapToChoicesFormat(values)
    const selector = selectorInstances[key]

    if (selector) {
      selector.setChoices(objValues, 'value', 'label', true)
      toggleSelectorState(key, objValues, key === 'subproducts' ? 'keine Subprodukte' : null)
    }
  })
}

/**
 * Initialize a Choices.js selector
 * @param {string} selector - CSS selector
 * @param {string} name - Name for storing in selectorInstances
 */
function initializeSelector(selector, name) {
  try {
    selectorInstances[name] = new window.Choices(selector, selectConfig)
  } catch (err) {
    console.error(`Failed to initialize selector ${name}:`, err)
  }
}

/**
 * Add event handlers for filter changes and removals
 */
function setupEventListeners() {
  // List of elements to add change listeners to
  const elements = [
    documentsSearchEl, productsEl, subproductsEl, industriesEl,
    productgroupsEl, certificatesEl, certificatetypesEl,
    certificateproducersEl, flyernamesEl
  ]

  // Add change event listeners
  elements.forEach((el) => {
    if (el) {
      el.addEventListener('change', updateFilter)
    }
  })

  // Handle removal of filters for dependent selectors
  if (productsEl) {
    productsEl.addEventListener('removeItem', () => {
      setValidProductNames(false)
      setValidProductGroupNames(false)
      setValidSubproductNames(false)
    })
  }

  if (subproductsEl) {
    subproductsEl.addEventListener('removeItem', () => {
      setValidProductNames(false)
      setValidSubproductNames(false)
      setValidProductGroupNames(false)
    })
  }

  if (productgroupsEl) {
    productgroupsEl.addEventListener('removeItem', () => {
      setValidProductNames(false)
      setValidSubproductNames(false)
      setValidProductGroupNames(false)
    })

    productgroupsEl.addEventListener('change', () => {
      updateProductsDropdown()
    })
  }
}

/**
 * Initialize all select dropdowns with Choices.js
 */
function initializeSelectors() {
  // Mapping of CSS class to instance name
  const selectorMapping = {
    '.js-documents__filter.industries': 'industries',
    '.js-documents__filter.productgroups': 'productgroups',
    '.js-documents__filter.products': 'products',
    '.js-documents__filter.subproducts': 'subproducts',
    '.js-documents__filter.flyernames': 'flyernames',
    '.js-documents__filter.certificates': 'certificates',
    '.js-documents__filter.certificateproducers': 'certificateproducers',
    '.js-documents__filter.certificatetypes': 'certificatetypes'
  }

  // Initialize all selectors
  Object.entries(selectorMapping).forEach(([selector, name]) => {
    initializeSelector(selector, name)
  })

  // Set up event handlers
  setupEventListeners()
}

// ---- Tabulator Table Configuration ----

/**
 * Custom sorter for document versions
 */
function versionSorter(a, b) {
  // Handle null values
  if (!a && !b) {
    return 0
  }
  if (!a) {
    return 1
  }
  if (!b) {
    return -1
  }

  // Pad and rearrange for sorting
  a = padNumber(a, 4)
  b = padNumber(b, 4)

  a = a.toString().slice(-2) + a.toString().slice(0, 2)
  b = b.toString().slice(-2) + b.toString().slice(0, 2)

  return parseInt(a, 10) - parseInt(b, 10)
}

/**
 * Custom sorter for string values
 */
function stringSorter(a, b) {
  // Handle null values
  if (!a && !b) {
    return 0
  }
  if (!a) {
    return 1
  }
  if (!b) {
    return -1
  }

  // Case insensitive string compare
  return a.toLowerCase().localeCompare(b.toLowerCase())
}

/**
 * Download tracking handler
 */
function handleDownloadClick(e, cell) {
  if (e.srcElement.nodeName === 'A') {
    try {
      zaraz.track('download', {
        document: {
          name: cell._cell.value.split('/').pop(),
          type: cell._cell.row.data.category,
          context: 'documentationList'
        }
      })
    } catch (e) {
      console.log('zaraz or zaraz.track not found?', e)
    }
  }
}

// ---- Table Columns ----

// Define table columns configuration
const tableColumns = [
  {
    title: plDocuments.dataset.i18nTitle,
    field: 'description',
    widthGrow: 3,
    formatter: title,
    responsive: 0
  },
  {
    title: plDocuments.dataset.i18nVersion,
    field: 'documentVersion',
    formatter: versionNumber,
    sorter: versionSorter,
    responsive: 4
  },
  {
    title: plDocuments.dataset.i18nLanguages,
    field: 'language',
    formatter: lang,
    responsive: 4
  },
  // {
  //   title: plDocuments.dataset.i18nType,
  //   field: 'type',
  //   sorter: stringSorter,
  //   widthGrow: 1.5,
  //   responsive: 2
  // },
  {
    title: plDocuments.dataset.i18nDownload,
    field: 'url',
    width: 140,
    formatter: downloadIcon,
    responsive: 0,
    headerSort: false,
    cellClick: handleDownloadClick
  },
  // Hidden columns for filtering
  {
    title: 'Industrien',
    field: 'industries',
    width: 500,
    visible: false,
    responsive: 0
  },
  {
    title: 'Productgroups',
    field: 'productGroups',
    width: 500,
    visible: false
  },
  {
    title: 'excludedCountries',
    field: 'excludedCountries',
    width: 500,
    visible: false
  },
  {
    title: 'certificateGroup',
    field: 'certificateGroup',
    width: 500,
    visible: false
  },
  {
    title: 'Produkte',
    field: 'products',
    width: 500,
    visible: false
  },
  {
    title: 'SubProdukte',
    field: 'subproducts',
    width: 500,
    visible: false
  },
  {
    title: 'partNumber',
    field: 'partNumber',
    visible: false
  },
  {
    title: 'flyerName',
    field: 'flyerName',
    visible: false
  },
  {
    title: 'linkDetailpage',
    field: 'linkDetailpage',
    visible: false
  }
]

// ---- Tabulator Table ----

const table = new Tabulator('#documents', {
  locale: true,
  ajaxURL: apiPath,
  height: 4000,
  ajaxResponse: (url, params, response) => trimObj(response),
  layout: 'fitColumns',
  responsiveLayout: 'hide',
  initialFilter: [
    createFilter('excludedCountries', 'notcontain', currentCountry)
  ],
  columns: tableColumns,
  langs: {
    'de-de': {
      columns: {
        name: 'Name'
      },
      ajax: {
        loading: 'wird geladen ...',
        error: 'Fehler'
      },
      groups: {
        item: 'item',
        items: 'items'
      },
      pagination: {
        page_size: 'Page Size',
        first: 'Erste',
        first_title: 'Erste Seite',
        last: 'Letzte',
        last_title: 'Letzte Seite',
        prev: 'weiter',
        prev_title: 'Seite weiter',
        next: 'zurück',
        next_title: 'Seite zurück'
      },
      headerFilters: {
        default: 'Spalte filtern',
        columns: {
          name: 'Name filtern'
        }
      }
    }
  }
})

// ---- Fetch Data and Initialize ----

/**
 * Fetch data from an API endpoint
 * @param {string} url - API endpoint URL
 * @param {Function} successCallback - Callback for successful fetch
 * @returns {Promise}
 */
function fetchData(url, successCallback) {
  if (!url) {
    return Promise.resolve()
  }

  return fetch(url, fetchOptions)
    .then((response) => response.json())
    .then(successCallback)
    .catch((error) => console.error(`Error fetching from ${url}:`, error))
}

// Initialize application
(function initialize() {
  // Initialize selectors
  initializeSelectors()

  // Fetch document data
  fetchData(apiPath, (data) => {
    updateSelectFilters(trimObj(data))
  })

  // Fetch related data
  const dataEndpoints = [
    { url: productsPath,
      dataKey: 'products' },
    { url: subproductsPath,
      dataKey: 'subproducts' },
    { url: productgroupsPath,
      dataKey: 'productgroups' }
  ]

  dataEndpoints.forEach(({ url, dataKey }) => {
    fetchData(url, (data) => {
      jsonData[dataKey] = mapJsonAssoc(data)
    })
  })
}())
